import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import Brand from "../api/Brand";
import Location from "../api/Location";
import Color from "../modules/Color";
import BrandSubscription from "../api/BrandSubscription";

export const useAppSetup = () => {
    const [cookies, setCookie, removeCookie] = useCookies([
        "uuid",
        "brand_id",
        "location_id",
        "theme_color",
    ]);

    const [authReady, setAuthReady] = useState(false);
    const [appReady, setAppReady] = useState(false);
    const [brand, setBrand] = useState<Brand | undefined>();
    const [location, setLocation] = useState<Location | undefined>();
    //const [brandIsSetUp, setBrandIsSetUp] = useState(false);
    const [needsSubscription, setNeedsSubscription] = useState(false);
    const [subscriptionExpired, setSubscriptionExpired] = useState(false);

    // 1. UUID restoration from localStorage → cookie
    useEffect(() => {
        const localUuid = localStorage.getItem("uuid");
        const isRedirecting = window.location.pathname.startsWith("/redirect");
        if (!isRedirecting && !cookies.uuid && localUuid) {
            setCookie("uuid", localUuid, {
                path: "/",
                maxAge: 30 * 24 * 60 * 60,
            });
            setTimeout(() => setAuthReady(true), 100);
        } else {
            setAuthReady(true);
        }
    }, [cookies.uuid, setCookie]);

    // 2. Business fetching (brand, location, setup state, theme)
    useEffect(() => {
        const cleanCookies = (withUUID = false) => {
            removeCookie("theme_color");
            removeCookie("brand_id");
            removeCookie("location_id");
            if (withUUID) {
                removeCookie("uuid");
                localStorage.removeItem("uuid");
            }
        };

        const fetchData = async () => {
            try {
                if (!cookies.uuid) {
                    cleanCookies();
                    return;
                }

                const brands = await Brand.list(cookies.uuid);
                if (!brands || brands.length === 0) {
                    cleanCookies(true);
                    return;
                }

                const selectedBrand = brands[0];
                setCookie("brand_id", selectedBrand.key, { path: "/" });

                const brandInfo = await Brand.info(
                    cookies.uuid,
                    selectedBrand.key
                );
                if (!brandInfo) {
                    cleanCookies(true);
                    return;
                }

                //setBrandIsSetUp(brandInfo.isSetUp);
                setBrand(brandInfo);

                const locations = await Location.list(
                    cookies.uuid,
                    selectedBrand.key
                );
                if (locations && locations.length > 0) {
                    setLocation(locations[0]);
                    setCookie("location_id", locations[0].key, { path: "/" });
                }

                // Generate and store theme color
                const color = new Color(
                    brandInfo.colorRed,
                    brandInfo.colorGreen,
                    brandInfo.colorBlue
                );
                setCookie("theme_color", color.toHex(), { path: "/" });

                // Subscription check
                const subscription = await BrandSubscription.info(
                    cookies.uuid,
                    selectedBrand.key
                );

                if (!subscription || Object.keys(subscription).length === 0) {
                    setNeedsSubscription(true);
                }

                if (subscription && !subscription.valid) {
                    setSubscriptionExpired(true);
                }

                setAppReady(true);
            } catch (err) {
                console.error("Error in app setup:", err);
            }
        };

        if (authReady && cookies.uuid) fetchData();
    }, [authReady, cookies.uuid, setCookie, removeCookie]);

    return {
        authReady,
        appReady,
        //brandIsSetUp,
        needsLogin: !cookies.uuid,
        //needsSetup: authReady && !brandIsSetUp,
        needsSubscription,
        subscriptionExpired,
        brand,
        location,
    };
};
